import axios from "axios";
import {
  clearLocalStorage,
  getAccessTokenSilently,
  getLocalStorageItem,
  setLocalStorageItem,
} from "utils/functions";

// Create Axios Client
const client = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

// Flag to avoid multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Async Request Interceptor (set up once)
client.interceptors.request.use(
  async (req) => {
    // ensure headers object exists
    req.headers = req.headers || {};

    // If caller already provided Authorization, keep it (allow overrides)
    if (!req.headers.Authorization) {
      try {
        const token = await getAccessTokenSilently();
        if (token) {
          req.headers.Authorization = `Bearer ${token}`;
        }
      } catch (err) {
        // If token fetch fails, just continue without token.
        // Optionally you could clear storage or redirect here.
      }
    }

    return req;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor with refresh logic
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    // If there's no config (something else failed), just reject
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // If 401 error and not retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue the request
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            // ensure headers exist
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // mark to avoid infinite loop
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Get refresh token from storage
        const authToken = getLocalStorageItem("userAuthData", true);
        if (!authToken?.refreshToken) {
          throw new Error("No refresh token available");
        }

        // Use plain axios to call refresh endpoint to avoid interceptor recursion
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/accounts/user/refresh-token`,
          { token: authToken.refreshToken }
        );

        const newToken = response?.data?.result?.token;
        if (!newToken) throw new Error("Refresh response missing token");

        const userAuthData = {
          token: newToken,
          refreshToken: authToken.refreshToken,
        };
        setLocalStorageItem("userAuthData", userAuthData);

        // update client default header so subsequent requests use it
        client.defaults.headers.common.Authorization = `Bearer ${newToken}`;

        // resolve queued requests
        processQueue(null, newToken);

        // retry original request with new token
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);
      } catch (err) {
        // reject queued requests
        processQueue(err, null);
        // clear user data and redirect to login
        clearLocalStorage();
        window.location.reload();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors, reject
    return Promise.reject(error);
  }
);

/**
 * Request Wrapper
 */
const request = (options) => {
  const onSuccess = (response) => response.data;

  const onError = (error) => {
    // normalize error: prefer response (axios) otherwise message
    return Promise.reject(error?.response ?? error?.message ?? error);
  };

  // allow callers to pass in headers.Authorization to override
  return client({
    ...options,
    headers: { ...(options?.headers || {}) },
  })
    .then(onSuccess)
    .catch(onError);
};

export default request;
